import React, { useState, useEffect, useCallback } from "react";
import { PushToTalkButton } from "./PushToTalkButton";
import { TranscriptPreview } from "./TranscriptPreview";
import { WorkflowFooter } from "./WorkflowFooter";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import {
  buildN8nWebhookUrl,
  isN8nWebhookConfigured,
  WEBHOOK_PATHS,
} from "../helpers/webhookConfig";
import { shouldSkipSupabase } from "../helpers/guestMode";

// Recording states
const STATE = {
  IDLE: "idle",
  RECORDING: "recording",
  REVIEW: "review",
};

export default function CockpitScreen({ onBackHome, session, currentUser }) {
  const [recordState, setRecordState] = useState(STATE.IDLE);

  const {
    isListening,
    transcript,
    error: speechError,
    isSupported,
    startListening,
    stopListening,
    clearTranscript,
  } = useSpeechRecognition();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [activeSession, setActiveSession] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Live runtime clock
  useEffect(() => {
    if (!activeSession) return;

    const interval = setInterval(() => {
      const start = new Date(activeSession.session_start).getTime();

      const now = Date.now();

      setElapsedSeconds(Math.floor((now - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [activeSession]);

  // Sync state when recognition ends
  useEffect(() => {
    if (!isListening && recordState === STATE.RECORDING) {
      setRecordState(STATE.REVIEW);
    }
  }, [isListening, recordState]);

  // Push-to-talk behavior
  const handlePTTPress = () => {
    if (recordState === STATE.IDLE) {
      setRecordState(STATE.RECORDING);
      startListening();
    } else if (recordState === STATE.RECORDING) {
      stopListening();
    }
  };

  // Send transcript to n8n
  const handleConfirm = async () => {
    if (!transcript || transcript.trim() === "") return;

    if (!isN8nWebhookConfigured() || shouldSkipSupabase(currentUser)) {
      alert("Sign in and configure voice intake to submit requests.");
      return;
    }

    const webhookUrl = buildN8nWebhookUrl(WEBHOOK_PATHS.INTAKE);
    if (!webhookUrl) {
      alert("Voice intake is not configured.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSuccessMessage("");

      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          transcript,
          raw_input: transcript,
          request_source: "voice",
          source: "cockpit-app",
          supabase_user_id: session?.user?.id || null,
          timestamp: new Date().toISOString(),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Webhook failed");
      }

      // START SESSION SUCCESS
      if (result.intent === "start_session") {
        setSuccessMessage(
          `Live session started on project ${result.project_id}`,
        );

        console.log("ACTIVE SESSION:", result);
        setActiveSession(result);
        setElapsedSeconds(0);
      }

      // STOP SESSION SUCCESS
      else if (
        result.intent === "stop_session" &&
        result.lifecycle_state === "pending_finalization"
      ) {
        setSuccessMessage(
          `Session stopped. Worked ${result.worked_minutes} minutes.`,
        );

        console.log("PENDING FINALIZATION:", result);
        setActiveSession(null);
        setElapsedSeconds(0);

        // NEXT:
        // launch end-of-day modal here
      }

      // DEFAULT SUCCESS
      else {
        setSuccessMessage(result.message || "Operation successful.");
      }

      clearTranscript();
      setRecordState(STATE.IDLE);
    } catch (err) {
      console.error(err);
      alert("Failed to send message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Continue listening
  const handleContinueListening = () => {
    setRecordState(STATE.RECORDING);
    startListening();
  };

  // Retry recording
  const handleRetry = () => {
    stopListening();
    clearTranscript();
    setRecordState(STATE.IDLE);
  };

  // Cancel/restart
  const handleCancel = () => {
    stopListening();
    clearTranscript();
    setRecordState(STATE.IDLE);
  };

  const handleHardwareBack = useCallback(() => {
    if (recordState === STATE.REVIEW) {
      handleCancel();
      return true;
    }

    if (recordState === STATE.RECORDING) {
      stopListening();
      setRecordState(STATE.IDLE);
      return true;
    }

    onBackHome?.();
    return true;
  }, [clearTranscript, onBackHome, recordState, stopListening]);

  useHardwareBackHandler(handleHardwareBack);

  return (
    <div className="screen question-screen nx-screen">
      {/* Top operational bar */}
      <div className="q-action-bar">
        <div className="q-action-bar-left">
          <div className="progress-bar nx-progress-bar">
            <div className="progress-track nx-progress-track">
              <div
                className="progress-fill nx-progress-fill"
                style={{ width: "100%" }}
              />
            </div>

            <span className="progress-label nx-progress-label">Cockpit</span>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="question-card nx-question-card">
        <p className="question-number nx-question-number">Cockpit</p>

        <h2 className="question-text nx-question-text">
          What would you like to log or report?
        </h2>

        {activeSession && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              border: "1px solid #d4af37",
              borderRadius: "12px",
              textAlign: "center",
            }}
          >
            <div>LIVE SESSION ACTIVE</div>

            <div style={{ marginTop: "6px" }}>
              Project: {activeSession.project_id}
            </div>

            <div style={{ marginTop: "6px", fontSize: "24px" }}>
              {Math.floor(elapsedSeconds / 3600)
                .toString()
                .padStart(2, "0")}
              :
              {Math.floor((elapsedSeconds % 3600) / 60)
                .toString()
                .padStart(2, "0")}
              :{(elapsedSeconds % 60).toString().padStart(2, "0")}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {speechError && (
        <div className="error-banner nx-error-banner" role="alert">
          <p>{speechError}</p>
        </div>
      )}

      {/* Browser support */}
      {!isSupported && (
        <div className="error-banner nx-error-banner" role="alert">
          <p>
            Voice input is not supported in your browser. Please use Chrome or
            Safari.
          </p>
        </div>
      )}

      {/* Main interaction */}
      <div className="interaction-area">
        {recordState !== STATE.REVIEW ? (
          <PushToTalkButton
            isListening={isListening}
            onPress={handlePTTPress}
            disabled={!isSupported || isSubmitting}
          />
        ) : (
          <TranscriptPreview
            transcript={transcript}
            isLoading={isSubmitting}
            onConfirm={handleConfirm}
            onContinue={handleContinueListening}
            onRetry={handleRetry}
            onBack={handleCancel}
            onCancel={handleCancel}
          />
        )}
      </div>

      {/* Instruction text */}
      {recordState === STATE.IDLE && (
        <p className="instruction-hint nx-instruction-hint">
          Tap the button above, speak your command, then tap again to stop.
        </p>
      )}

      {/* Success */}
      {successMessage && (
        <div
          style={{
            marginTop: "12px",
            textAlign: "center",
            color: "#d4af37",
          }}
        >
          {successMessage}
        </div>
      )}

      {/* Bottom navigation */}
      {recordState === STATE.IDLE && (
        <WorkflowFooter
          onBack={onBackHome}
          onHome={onBackHome}
          showBack={true}
          backLabel="Back"
          homeLabel="Home"
        />
      )}
    </div>
  );
}
